<template>
  <div class="h-full">
    <vue-progress-bar class="progress-bar" />

    <Loader
      v-if="$apollo.loading || loading"
      class="loader v-center"
      label="Loading Preview..."
    />

    <ViewportSelector v-if="!$apollo.loading && !loading && previewUrl" />

    <div
      v-if="!$apollo.loading && !loading && !previewUrl"
      class="text-center text-grey h-full leading-none"
    >
      <div class="v-center">
        <Error
          v-if="errors.length"
          :errors="errors"
          class="error"
          message="Preview currently unavailable"
          :error-action="onManualRefresh"
          error-action-label="Reload?"
        />
      </div>
    </div>

    <div class="preview-container">
      <iframe
        v-if="!$apollo.loading && !loading && previewUrl"
        v-lozad="previewUrl"
        frameborder="0"
        class="preview-frame"
        :class="classObject"
        scrolling="yes"
        tabindex="-1"
        sandbox="allow-same-origin allow-forms allow-popups allow-scripts allow-modals"
        @load="onLoad"
      />
    </div>
  </div>
</template>

<script>
import { Preview } from "@/graphql/queries";
import Loader from "@/components/UI/Loader";
import Error from "@/components/UI/Error";
import EventBus from "@/lib/event-bus.js";
import ViewportSelector from "@/components/Editor/ViewportSelector";

export default {
  apollo: {
    previewUrl: {
      query: () => Preview,
      update(data) {
        return data?.preview?.previewUrl ?? "";
      },
      variables() {
        return {
          input: this.queryInput,
        };
      },
      error(error) {
        this.errors = [];
        this.errors.push(error);
      },
    },
  },
  name: "Preview",
  components: { Error, Loader, ViewportSelector },
  props: {
    mobileSidebar: { type: Boolean, default: false },
    queryInput: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      errors: [],
      loading: false,
      previewDevice: "desktop",
      previewUrl: "",
    };
  },
  computed: {
    classObject() {
      return {
        [this.previewDevice]: this.previewDevice,
        "sidebar-active": this.mobileSidebar,
      };
    },
  },
  watch: {
    previewUrl(newUrl, oldUrl) {
      this.loading = false;

      if (newUrl && newUrl !== oldUrl) {
        this.$Progress.start();
      }
    },
  },
  mounted() {
    this.setupEventBus();
  },
  beforeDestroy() {
    this.removeEventBus();
  },
  methods: {
    onLoad() {
      this.$Progress.finish();

      // Emit a global event on the event bus so distant components know when the preview has finished loading...
      EventBus.$emit("preview-loading", false);

      // Emit a local event so parent components know when the preview has finsihed loading...
      this.$emit("preview-loaded", this.previewUrl);
    },
    onManualRefresh() {
      this.$apollo.queries.previewUrl.refresh();
    },
    onViewportSelected(device) {
      if (!device) return false;

      this.previewDevice = device;
    },
    removeEventBus() {
      EventBus.$off("refetch-preview");
      EventBus.$off("preview-loading");
      EventBus.$off("viewport-selected");
    },
    setupEventBus() {
      EventBus.$on("preview-loading", (loading) => {
        if (loading) this.loading = true;
      });

      EventBus.$on("refetch-preview", () => {
        this.$Progress.start();
        this.$apollo.queries.previewUrl.refetch();
      });

      EventBus.$on("viewport-selected", (device) => {
        this.onViewportSelected(device);
      });
    },
  },
};
</script>

<style scoped>
.preview-container {
  @apply overflow-auto h-full p-2 mx-auto;
  -webkit-overflow-scrolling: touch;
  max-width: 800px;

  @screen xl {
    @apply pb-6;
  }
}

.preview-frame {
  @apply rounded-lg shadow bg-white block h-full my-0 mx-auto max-w-full p-0 w-full border border-grey-lighter;
  transition: max-width 300ms cubic-bezier(0.55, 0.055, 0.675, 0.19),
    filter 300ms ease-in;

  &.sidebar-active {
    filter: blur(4px);

    @screen md {
      filter: none;
    }
  }
}

.desktop {
  max-width: 100%;
}

.tablet {
  max-width: 500px;
}

.phone {
  max-width: 320px;
}

.progress-bar {
  @apply w-full;
  position: absolute !important;
  z-index: 2;
}

.error {
  @apply max-w-sm mx-auto;
}

>>> .viewport-selectors {
  @apply mt-2 mb-0;

  .viewport-selector.active {
    @apply text-brand bg-transparent;
  }
}
</style>
